import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { ApiError } from '../api/client';
import { getMe, sendSessionActivity, type MeResponse } from '../api/management';
import { Loader } from '../components/ui/Loader';

type AdminSessionContextValue = {
  session: MeResponse;
  refresh: () => Promise<void>;
};

const AdminSessionContext = createContext<AdminSessionContextValue | null>(null);

export function useAdminSession() {
  const value = useContext(AdminSessionContext);
  if (!value) throw new Error('useAdminSession must be used inside AdminSessionGuard');
  return value;
}

type AdminSessionGuardProps = {
  fallback?: ReactNode;
};

export default function AdminSessionGuard({ fallback = null }: AdminSessionGuardProps) {
  const [session, setSession] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const mounted = useRef(true);

  const load = async () => {
    let redirecting = false;
    setLoading(true);
    setError('');
    try {
      const result = await getMe();
      if (mounted.current) setSession(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // apiRequest owns the unauthenticated redirect. Keep the guard in its
        // neutral loading state so an expected first-visit 401 never flashes
        // the recovery UI while the browser is navigating to /login/.
        redirecting = true;
        return;
      }
      if (mounted.current) setError(err instanceof Error ? err.message : 'Session check failed');
    } finally {
      if (mounted.current && !redirecting) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!session || !['ADMIN', 'SUPER_MASTER', 'MASTER', 'DISTRIBUTOR'].includes(session.user.role)) return;
    let lastSent = 0;
    const send = () => {
      const now = Date.now();
      if (now - lastSent < 60_000) return;
      lastSent = now;
      void sendSessionActivity().catch(() => undefined);
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((eventName) => window.addEventListener(eventName, send, { passive: true }));
    const onVisibility = () => { if (document.visibilityState === 'visible') send(); };
    const onFocus = () => send();
    const onPageShow = () => send();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, send));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [session]);

  const value = useMemo<AdminSessionContextValue | null>(() => session ? ({ session, refresh: load }) : null, [session]);

  if (loading) return <Loader fullScreen label="Checking admin session…" />;
  if (error) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-100 p-4">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-bold text-slate-900">Session check failed</h1>
          <p className="mt-2 text-sm text-red-700">{error}</p>
          <button type="button" className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white" onClick={() => void load()}>Retry</button>
        </div>
      </main>
    );
  }
  if (!session) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-100 p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-bold text-slate-900">Session unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">Your session could not be verified. Retry the session check or sign in again.</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <button type="button" className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white" onClick={() => void load()}>Retry</button>
            <a className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800" href="/login/">Sign in</a>
          </div>
        </div>
      </main>
    );
  }
  if (!['ADMIN', 'SUPER_MASTER', 'MASTER', 'DISTRIBUTOR'].includes(session.user.role)) return <>{fallback}</>;

  return <AdminSessionContext.Provider value={value}><Outlet /></AdminSessionContext.Provider>;
}
